import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
  CenteredCircularProgress,
  type DraftExecutivePosition,
  ExecutivePositionForm,
  IconButtonWithTooltip,
  PersonSelectDialog,
  PersonMiniCard,
  useExecPositionTypeLuts,
  useGlobalMessageHandler,
  useProtectedRoleHoldersLut
} from 'components'
import { useSessionContext } from 'session'
import { formatErrorFieldMessages } from 'interfaces'
import { type ExecutiveOrganization, getExecutiveOrganization, getProtectedRoleHolders } from 'services'
import { createExecutivePosition } from 'services/executives'
import { type ExecutivePosition } from 'services/person/us/edit/executiveRolesService'
import { type PersonBaseBasicInformation } from 'services/person/international/edit/personBasicInfoService'
import { getEmptyPersonBasicInfo, isExecPositionRowVacant } from 'helpers'
import { getExecPositionFieldErrors, isStateExecutiveRole } from 'utils'

const NORMAL_STATUS_ID = 1
const VACANT_CREATE_STATUS_ID = 10
const OTHER_APPT_TYPE_CODE = 'OTH'
const NO_POSITION_TYPE_ID = 0

const buildNewPosition = (
  org: ExecutiveOrganization,
  person: PersonBaseBasicInformation,
  typeId: number
): DraftExecutivePosition => ({
  id: 0,
  title: '',
  org: { id: org.id, name: org.name, code: org.code, topId: org.topOrg.id },
  execRole: null,
  branchRoleId: org.role.id,
  execRole2: null,
  execRole3: null,
  statusId: NORMAL_STATUS_ID,
  state: org.state,
  apptStartDate: null,
  apptEndDate: null,
  displayOrder: 0,
  person: { ...person, firstName: person.firstName ?? '' },
  typeId,
  electDate: null,
  electCode: null,
  percentVote: null,
  reelectYear: null,
  termNumber: null,
  cco: 0,
  nominateDate: null,
  confirmDate: null,
  levelId: null,
  presidentId: null,
  liaisonCode: 0,
  addressId: null,
  responsibilities: [],
  administrationIds: []
})

export const ExecutivePositionCreatePage: React.FC = () => {
  const { execOrgId } = useParams<{ execOrgId: string }>()
  const parsedExecOrgId = execOrgId != null ? parseInt(execOrgId, 10) : 0
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const isVacantMode = searchParams.get('vacant') === 'true'
  const { changeModuleName, changeUniqueId } = useSessionContext()
  const { showErrorMessage, showSuccessMessage } = useGlobalMessageHandler()
  const { lutValues: execPosTypeLut } = useExecPositionTypeLuts()
  const { setLutValues: setProtectedRoleHoldersLut } = useProtectedRoleHoldersLut()
  const [org, setOrg] = useState<ExecutiveOrganization | null>(null)
  const [loading, setLoading] = useState(true)
  const [person, setPerson] = useState<PersonBaseBasicInformation | null>(null)
  const [position, setPosition] = useState<DraftExecutivePosition | null>(null)
  const [showPersonDialog, setShowPersonDialog] = useState(!isVacantMode)
  const [saving, setSaving] = useState(false)

  useEffect(() => { changeModuleName('Add Executive Position') }, [changeModuleName])

  useEffect(() => {
    if (Number.isNaN(parsedExecOrgId) || parsedExecOrgId === 0) {
      setLoading(false)
      return
    }
    getExecutiveOrganization(parsedExecOrgId)
      .then(setOrg)
      .catch(() => { showErrorMessage('Failed to load executive organization.') })
      .finally(() => { setLoading(false) })
  }, [parsedExecOrgId, showErrorMessage])

  useEffect(() => {
    changeUniqueId(org != null ? `${org.id} - ${org.name}` : '')
  }, [org, changeUniqueId])

  useEffect(() => {
    if (position != null || org == null) return
    if (!isVacantMode && person == null) return
    if (execPosTypeLut.execPosTypes.length === 0) return
    const otherApptType = execPosTypeLut.execPosTypes.find(t => t.code === OTHER_APPT_TYPE_CODE)
    const defaultTypeId = isStateExecutiveRole(org.role.id) && otherApptType != null
      ? otherApptType.id
      : NO_POSITION_TYPE_ID
    const holder = isVacantMode ? { ...getEmptyPersonBasicInfo(), lastName: 'Vacant' } : person
    if (holder == null) return
    const newPosition = buildNewPosition(org, holder, defaultTypeId)
    setPosition(
      isVacantMode
        ? { ...newPosition, statusId: VACANT_CREATE_STATUS_ID, vacant: true }
        : newPosition
    )
  }, [position, org, person, execPosTypeLut, isVacantMode])

  if (Number.isNaN(parsedExecOrgId) || parsedExecOrgId === 0) {
    return <Box>Invalid Executive Organization ID</Box>
  }
  if (loading) {
    return <CenteredCircularProgress loading={loading} size={100} position='absolute' />
  }
  if (org == null) {
    return <Box>Executive Organization not found.</Box>
  }

  const vacantPerson = !isVacantMode && position != null && isExecPositionRowVacant(position.person)
  const electionBlocked = position != null &&
    Object.keys(getExecPositionFieldErrors(position, isVacantMode || vacantPerson)).length > 0

  const update = (changes: Partial<DraftExecutivePosition>): void => {
    setPosition(prev => (prev != null ? { ...prev, ...changes } : prev))
  }

  const handlePersonSelected = (selected: PersonBaseBasicInformation): void => {
    setShowPersonDialog(false)
    setPerson(selected)
  }

  const handleSave = (): void => {
    if (position == null || position.execRole == null) return
    const payload: ExecutivePosition = { ...position, execRole: position.execRole }
    setSaving(true)
    createExecutivePosition(payload)
      .then(response => {
        if (response.succeeded && response.result != null) {
          showSuccessMessage('Position created.')
          getProtectedRoleHolders().then(setProtectedRoleHoldersLut).catch(() => { showErrorMessage('Could not refresh the protected-role holders hint; it may be stale.') })
          navigate(`/us/executives/position-update/${response.result.id}`)
        } else {
          const errorMessage = formatErrorFieldMessages(response.errors)
          showErrorMessage(errorMessage !== '' ? errorMessage : 'Failed to create position.')
        }
      })
      .finally(() => { setSaving(false) })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
      <Stack spacing={2} mt={1} width='98%'>
        <Card>
          <CardContent>
            <Typography variant='h6' sx={{ mb: 2 }}>{isVacantMode ? `New Vacant Position — ${org.name}` : `New Position — ${org.name}`}</Typography>
            {position == null
              ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  {!isVacantMode && (
                    <Button variant='contained' startIcon={<PersonAddIcon />} onClick={() => { setShowPersonDialog(true) }}>
                      Select Person
                    </Button>
                  )}
                </Box>
                )
              : (
                <>
                  <ExecutivePositionForm
                    position={position}
                    update={update}
                    fieldsDisabled={false}
                    addressRequired
                    isVacant={isVacantMode}
                    disableTenureFields={isVacantMode}
                    holder={
                      <Grid container spacing={1} alignItems='center'>
                        <Grid item xs={8}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Typography variant='subtitle1' sx={{ whiteSpace: 'nowrap' }}>Current Holder:</Typography>
                            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                              <PersonMiniCard
                                person={position.person}
                                filled={!isVacantMode}
                                withPhoto
                                fullWidth
                              />
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    }
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 4 }}>
                    {position.typeId === NO_POSITION_TYPE_ID && (
                      <Typography variant='body2' color='warning.main' sx={{ mr: 2 }}>
                        Select a Position Type to save.
                      </Typography>
                    )}
                    {position.execRole == null && (
                      <Typography variant='body2' color='warning.main' sx={{ mr: 2 }}>
                        Select an Executive Role to save.
                      </Typography>
                    )}
                    {position.addressId == null && position.address == null && (
                      <Typography variant='body2' color='warning.main' sx={{ mr: 2 }}>
                        An address is required to save. Use “Add Address”, next to the Address field, to attach one.
                      </Typography>
                    )}
                    {vacantPerson && (
                      <Typography variant='body2' color='warning.main' sx={{ mr: 2 }}>
                        Cannot assign a vacant person to a position.
                      </Typography>
                    )}
                    {electionBlocked && (
                      <Typography variant='body2' color='warning.main' sx={{ mr: 2 }}>
                        Fill in the election fields or change Position Type to save.
                      </Typography>
                    )}
                    {saving && <CircularProgress size={28} sx={{ alignSelf: 'center', mr: 1 }} />}
                    <IconButtonWithTooltip
                      label='Save Position'
                      color='primary'
                      onClick={handleSave}
                      disabled={saving || position.typeId === NO_POSITION_TYPE_ID || position.execRole == null || (position.addressId == null && position.address == null) || position.title.trim() === '' || vacantPerson || electionBlocked}
                      icon={<SaveIcon />}
                    />
                  </Box>
                </>
                )}
          </CardContent>
        </Card>
      </Stack>
      {!isVacantMode && (
        <PersonSelectDialog<PersonBaseBasicInformation>
          open={showPersonDialog}
          onClose={() => { setShowPersonDialog(false) }}
          onPersonSelected={handlePersonSelected}
          title='Add Position — Select Person'
          personType='personUS'
        />
      )}
    </Box>
  )
}
